import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

interface VoiceNotePlayerProps {
  /**
   * API path of the audio, e.g. `/tasks/:id/voice-note` for a task's briefing or
   * `/tasks/timeline/:id/voice-note` for a reviewer's remark. A path rather than a task
   * id because the same player serves both.
   */
  src: string;
  /** Section heading. Hidden when null — a review round labels itself. */
  label?: string | null;
}

/**
 * Plays a voice note. The endpoints are permission-gated, so the audio cannot be handed
 * to <audio src> as a plain URL — it is fetched through the authed axios instance and
 * played from an object URL, the same approach as AuthImage.
 *
 * Fetching the whole blob also means seeking works client-side, so the server needs
 * no HTTP Range support.
 */
export function VoiceNotePlayer({ src, label = 'Voice Note' }: VoiceNotePlayerProps) {
  // Each result is tagged with the src it belongs to, so switching tasks falls back
  // to the loading state on its own — no synchronous reset inside the effect, which
  // would cause a cascading render.
  const [loaded, setLoaded] = useState<{ src: string; url: string } | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;

    api
      .get<Blob>(src, {
        responseType: 'blob',
        signal: controller.signal,
      })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setLoaded({ src, url: objectUrl });
      })
      .catch((err: { code?: string }) => {
        if (err?.code !== 'ERR_CANCELED') setFailedFor(src);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const blobUrl = loaded?.src === src ? loaded.url : null;
  const failed = failedFor === src;

  return (
    <div>
      {label && (
        <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
      )}
      <div className="rounded-lg border border-border/60 bg-card p-1.5">
        {failed ? (
          <p className="px-1 py-0.5 text-xs text-muted-foreground">Couldn’t load this voice note.</p>
        ) : !blobUrl ? (
          // Same height as the control it becomes, so nothing jumps when it loads.
          <div className="h-8 animate-pulse rounded bg-muted/60" />
        ) : (
          // No separate duration line: the native control already prints "0:00 / 0:03",
          // so a second copy underneath was the same number twice.
          <audio controls src={blobUrl} className="h-8 w-full" />
        )}
      </div>
    </div>
  );
}
